<template>
  <div v-if="visible" class="lm-overlay">
    <div class="lm-card">
      <!-- 头部：标题 + 关闭 -->
      <div class="lm-head">
        <div class="lm-title">
          <span class="lm-dot"></span>
          <span>{{
            loginQr.loading ? '正在准备登录…' : '扫码登录企业微信'
          }}</span>
          <span v-if="loginQr.kind === 'screen'" class="lm-tag">实时屏幕</span>
        </div>
        <button @click="close" class="lm-x" title="关闭窗口">✕</button>
      </div>

      <!-- meta 行：设备号 + 尺寸 -->
      <div class="lm-meta">
        <template v-if="loginQr.clientId"
          ><code>{{ loginQr.clientId }}</code> · {{ loginQr.width }}×{{
            loginQr.height
          }}</template
        >
        <template v-else>等待设备响应…</template>
      </div>

      <!-- 图像区：占位 / QR / 屏幕 共用 -->
      <div class="lm-frame">
        <div v-if="loginQr.loading" class="lm-loading">
          <div class="lm-spin"></div>
          <div class="lm-loading-t">正在初始化</div>
          <div class="lm-loading-s">
            清缓存 + 改机 + 启动企微 + 拉 QR · 约 30 秒
          </div>
        </div>
        <template v-else>
          <img
            v-if="loginQr.pngBase64"
            class="lm-img"
            :src="'data:image/png;base64,' + loginQr.pngBase64"
            :alt="loginQr.kind === 'screen' ? 'Device Screen' : 'QR Login'"
          />
          <div v-else class="lm-empty">图像为空（screencap 失败）</div>
        </template>
      </div>

      <!-- focus 行：仅 screen 模式显示，单行截断 -->
      <div
        v-if="
          !loginQr.loading &&
          loginQr.kind === 'screen' &&
          loginQr.focusedActivity
        "
        class="lm-focus"
        :title="loginQr.focusedActivity"
      >
        <span class="lm-focus-l">focus</span>
        <code>{{ loginQr.focusedActivity }}</code>
      </div>

      <!-- 工具按钮组：放图下面，左右分布 -->
      <div class="lm-tools">
        <button
          @click="captureDeviceScreen"
          :disabled="capturingScreen || loginQr.loading"
          class="lm-ghost"
        >
          <span v-if="capturingScreen" class="lm-mini-spin"></span>
          {{ capturingScreen ? '刷新中…' : '刷新设备屏幕' }}
        </button>
        <button
          @click="startPadQrLogin"
          :disabled="loginStarting"
          class="lm-ghost"
          title="重头走一遍：清缓存 + 改机 + 拉新 QR"
        >
          <span v-if="loginStarting" class="lm-mini-spin"></span>
          {{ loginStarting ? '重启中…' : '重新生成 QR' }}
        </button>
      </div>

      <!-- 步骤提示：单行紧凑 -->
      <div v-if="!loginQr.loading" class="lm-steps">
        <span><b>1</b> 真账号扫码</span>
        <span><b>2</b> 收 6 位短信</span>
        <span><b>3</b> 填入下方提交</span>
      </div>

      <!-- 验证码输入 -->
      <div class="lm-input-row">
        <input
          v-model="loginCodeInput"
          maxlength="6"
          inputmode="numeric"
          pattern="\d{6}"
          placeholder="—  —  —  —  —  —"
          :readonly="loginSubmittedAt !== null || loginQr.loading"
          class="lm-input"
          :class="{
            'lm-input-locked': loginSubmittedAt !== null || loginQr.loading
          }"
          @keyup.enter="submitPadSmsCode"
        />
        <button
          v-if="loginSubmittedAt === null"
          @click="submitPadSmsCode"
          :disabled="
            loginSubmitting || loginQr.loading || loginCodeInput.length !== 6
          "
          class="lm-submit"
        >
          <span
            v-if="loginSubmitting"
            class="lm-mini-spin lm-mini-spin-w"
          ></span>
          {{ loginSubmitting ? '提交中' : '提交' }}
        </button>
        <button
          v-else
          @click="
            loginSubmittedAt = null
            loginResult = null
          "
          class="lm-redo"
        >
          重输
        </button>
      </div>

      <!-- 状态提示 -->
      <div v-if="loginSubmittedAt !== null" class="lm-tip lm-tip-info">
        <span
          >已下发 <code>{{ loginCodeInput }}</code
          >，等待设备确认…</span
        >
      </div>
      <div
        v-if="loginResult && !loginResult.success && loginResult.soft"
        ref="loginTip"
        class="lm-tip lm-tip-warn"
      >
        <code>{{ loginResult.code }}</code
        ><span>{{ loginResult.error || '（设备未返回详细原因）' }}</span>
      </div>
      <div
        v-if="loginResult && !loginResult.success && !loginResult.soft"
        ref="loginTip"
        class="lm-tip lm-tip-err"
      >
        <code>{{ loginResult.code }}</code
        ><span>{{ loginResult.error || '（设备未返回详细原因）' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getLoginQrcodeRequest,
  getLoginQrcodeImage,
  // cancelLoginQrcode,
  submitLoginVerifyCode,
  getLoginQrcodeStatus
} from '@/api/smartSalesPhone/cloudPhone'
export default {
  name: 'WxLogin',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    deviceId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loginQr: {
        loading: true,
        kind: 'qr',
        pngBase64: '',
        clientId: '',
        width: 1080,
        height: 1920,
        focusedActivity: ''
      },
      capturingScreen: false,
      loginStarting: false,
      loginCodeInput: '',
      loginSubmittedAt: null,
      loginSubmitting: false,
      loginResult: null,
      loginId: null,
      pollingTimer: null
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetState()
        this.startPadQrLogin()
      }
    }
  },
  methods: {
    async handleCloseCleanup() {
      this.clearPolling()
      // try {
      //   await cancelLoginQrcode({ loginId: this.deviceId })
      // } catch (e) {
      //   console.error('取消登录二维码异常', e)
      // }
    },
    clearPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    close() {
      this.$emit('update:visible', false)
      this.handleCloseCleanup()
    },
    async resetState() {
      this.clearPolling()
      this.loginId = null
      this.loginQr = {
        loading: true,
        kind: 'qr',
        pngBase64: '',
        clientId: this.deviceId || 'unknown',
        width: 1080,
        height: 1920,
        focusedActivity: ''
      }
      this.capturingScreen = false
      this.loginStarting = false
      this.loginCodeInput = ''
      this.loginSubmittedAt = null
      this.loginSubmitting = false
      this.loginResult = null
    },
    async startPadQrLogin() {
      this.clearPolling()
      this.loginStarting = true
      this.loginQr.loading = true

      try {
        const res = await getLoginQrcodeRequest({
          deviceId: this.deviceId || 'unknown'
        })
        if (res.code === 1 || res.code === 200) {
          this.loginId = res.data?.loginId
          this.startPollingImageLoop()
        } else {
          this.$message.error(res.msg || '获取登录二维码失败')
          this.loginStarting = false
          this.loginQr.loading = false
        }
      } catch (e) {
        this.$message.error('请求获取登录二维码异常')
        this.loginStarting = false
        this.loginQr.loading = false
      }
    },
    startPollingImageLoop() {
      this.clearPolling()
      let pollCount = 0
      this.pollingTimer = setInterval(async () => {
        pollCount++
        if (pollCount >= 40) {
          this.clearPolling()
          this.loginQr.loading = false
          this.loginStarting = false
          this.$message.warning('获取二维码超时，请重试')
          return
        }
        try {
          await this.getLoginQrcodeImageAsync()
        } catch (error) {
          console.error('获取登录二维码图片异常:', error)
        }
      }, 1000)
    },
    async getLoginQrcodeImageAsync() {
      const res = await getLoginQrcodeImage({ loginId: this.loginId })
      if (res.code === 1) {
        if (res.data) {
          this.loginQr.pngBase64 = res.data.imageBase64
          this.loginQr.loading = false
          this.loginStarting = false
          clearInterval(this.pollingTimer)
          this.pollingTimer = null
        }
      }
    },
    async captureDeviceScreen() {
      this.capturingScreen = true
      // 模拟请求
      this.getLoginQrcodeImageAsync()
      setTimeout(() => {
        this.loginQr.kind = 'screen'
        this.loginQr.focusedActivity =
          'com.tencent.wework.launch.WwMainActivity'
        this.capturingScreen = false
      }, 1000)
    },
    async submitPadSmsCode() {
      if (this.loginCodeInput.length !== 6) return
      this.loginSubmitting = true
      this.loginSubmittedAt = new Date()
      this.loginResult = null

      try {
        const res = await submitLoginVerifyCode({
          loginId: this.loginId,
          verifyCode: this.loginCodeInput
        })
        if (res.code === 1 || res.code === 200) {
          this.startPollingStatusLoop()
        } else {
          this.loginSubmitting = false
          this.loginResult = {
            success: false,
            soft: false,
            code: 'ERR_SUBMIT',
            error: res.msg || '验证码提交失败'
          }
        }
      } catch (e) {
        this.loginSubmitting = false
        this.loginResult = {
          success: false,
          soft: false,
          code: 'ERR_NETWORK',
          error: '网络请求异常'
        }
      }
    },
    startPollingStatusLoop() {
      this.clearPolling()
      let pollCount = 0
      this.pollingTimer = setInterval(async () => {
        pollCount++
        // 超时判断 (例如 60 秒)
        if (pollCount >= 60) {
          this.clearPolling()
          this.loginSubmitting = false
          this.loginResult = {
            success: false,
            soft: true,
            code: 'ERR_TIMEOUT',
            error: '设备未返回结果，超时'
          }
          return
        }
        try {
          if (!this.loginId) return
          const res = await getLoginQrcodeStatus({ loginId: this.loginId })
          if (res.code === 1 || res.code === 200) {
            const status = res.data?.status ?? res.data
            // 状态码: 0已扫码待确认 1需要验证码 2需要验证码 3验证码已输入, 正在登录 4登录成功 5 登录失败  6已过期 7已取消
            if (status === 4) {
              this.clearPolling()
              this.loginSubmitting = false
              this.loginResult = {
                success: true,
                code: 'ERR_SUCCESS',
                error: '登录成功'
              }
              this.$message.success('登录成功')
              this.$emit('update:visible', false)
              this.$emit('loginSuccess', { ...res.data, loginId: this.loginId, deviceId: this.deviceId })
            } else if ([5, 6].includes(status)) {
              this.clearPolling()
              this.loginSubmitting = false
              const errorMap = {
                5: '登录失败',
                6: '已过期'
              }
              this.loginResult = {
                success: false,
                soft: false,
                code: `ERR_STATUS_${status}`,
                error: errorMap[status] || '登录异常'
              }
            }
            // 其他状态 (0, 1, 2, 3, 4) 继续轮询
          }
        } catch (error) {
          console.error('获取登录状态异常:', error)
        }
      }, 1000)
    }
  }
}
</script>

<style scoped>
/* ============ 登录 modal ============ */
.lm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
  animation: lmFade 0.18s ease-out;
}
@keyframes lmFade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
.lm-card {
  background: #fff;
  border-radius: 14px;
  padding: 22px;
  width: 420px;
  max-width: 92vw;
  box-shadow: 0 20px 60px -20px rgba(15, 23, 42, 0.4),
    0 1px 3px rgba(15, 23, 42, 0.1);
  max-height: 92vh;
  overflow-y: auto;
}
.lm-card::-webkit-scrollbar {
  width: 6px;
}
.lm-card::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 3px;
}

.lm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.lm-title {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}
.lm-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.15);
  flex: none;
  animation: lmPulse 1.6s ease-in-out infinite;
}
@keyframes lmPulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.15);
    opacity: 0.7;
  }
}
.lm-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  letter-spacing: 0.02em;
}
.lm-x {
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 6px 9px;
  border-radius: 7px;
  transition: 0.12s;
}
.lm-x:hover {
  background: #f1f5f9;
  color: #475569;
}

.lm-meta {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 14px;
  font-family: -apple-system, sans-serif;
}
.lm-meta code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #475569;
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11.5px;
}

.lm-frame {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 8px;
  text-align: center;
  margin-bottom: 10px;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lm-img {
  max-width: 100%;
  max-height: 380px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.lm-empty {
  padding: 60px 20px;
  color: #dc2626;
  font-size: 13px;
  background: #fef2f2;
  border-radius: 6px;
  width: 100%;
}
.lm-loading {
  padding: 50px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.lm-spin {
  width: 32px;
  height: 32px;
  border: 3px solid #e0f2fe;
  border-top-color: #0ea5e9;
  border-radius: 50%;
  animation: sp 0.8s linear infinite;
}
.lm-loading-t {
  color: #334155;
  font-size: 14px;
  font-weight: 600;
  margin-top: 4px;
}
.lm-loading-s {
  color: #94a3b8;
  font-size: 12px;
}

.lm-focus {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 10px;
  padding: 6px 10px;
  background: #f8fafc;
  border-radius: 6px;
  overflow: hidden;
}
.lm-focus-l {
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex: none;
}
.lm-focus code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.lm-tools {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}
.lm-ghost {
  flex: 1;
  padding: 8px 12px;
  background: #fff;
  color: #475569;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: 0.12s;
}
.lm-ghost:hover:not(:disabled) {
  border-color: #0ea5e9;
  color: #0ea5e9;
  background: #f0f9ff;
}
.lm-ghost:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.lm-mini-spin {
  width: 11px;
  height: 11px;
  border: 2px solid rgba(14, 165, 233, 0.3);
  border-top-color: #0ea5e9;
  border-radius: 50%;
  animation: sp 0.7s linear infinite;
  flex: none;
}
.lm-mini-spin-w {
  border-color: rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
}

.lm-steps {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  background: #f8fafc;
  padding: 9px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}
.lm-steps span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.lm-steps b {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #0ea5e9;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}

.lm-input-row {
  display: flex;
  gap: 8px;
}
.lm-input {
  flex: 1;
  padding: 11px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 9px;
  font-size: 18px;
  letter-spacing: 8px;
  text-align: center;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-weight: 600;
  color: #0f172a;
  outline: none;
  transition: 0.12s;
}
.lm-input:focus {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}
.lm-input::placeholder {
  color: #cbd5e1;
  letter-spacing: 6px;
  font-weight: 400;
}
.lm-input-locked {
  background: #f8fafc;
  color: #64748b;
}
.lm-submit {
  padding: 0 22px;
  background: linear-gradient(135deg, #0ea5e9, #06b6d4);
  color: #fff;
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 88px;
  justify-content: center;
  transition: 0.12s;
}
.lm-submit:hover:not(:disabled) {
  box-shadow: 0 4px 12px -2px rgba(14, 165, 233, 0.5);
}
.lm-submit:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.lm-redo {
  padding: 0 18px;
  background: #f1f5f9;
  color: #475569;
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}
.lm-redo:hover {
  background: #e2e8f0;
}

.lm-tip {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.55;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  word-break: break-word;
}
.lm-tip code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11.5px;
  padding: 1px 6px;
  border-radius: 4px;
  flex: none;
  align-self: flex-start;
  margin-top: 1px;
}
.lm-tip > span {
  flex: 1;
  min-width: 0;
}
.lm-tip-info {
  background: #eff6ff;
  color: #1d4ed8;
  border-left: 3px solid #3b82f6;
}
.lm-tip-info code {
  background: #dbeafe;
  color: #1e40af;
}
.lm-tip-warn {
  background: #fffbeb;
  color: #92400e;
  border-left: 3px solid #f59e0b;
}
.lm-tip-warn code {
  background: #fef3c7;
  color: #78350f;
}
.lm-tip-err {
  background: #fef2f2;
  color: #b91c1c;
  border-left: 3px solid #ef4444;
}
.lm-tip-err code {
  background: #fee2e2;
  color: #991b1b;
}
.lm-tip-flash {
  animation: lmFlash 1.4s ease-out;
}
@keyframes lmFlash {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.55);
  }
  60% {
    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}
@keyframes sp {
  to {
    transform: rotate(360deg);
  }
}
</style>
